import * as React from "react";
import { styled, useTheme, Theme, CSSObject, alpha } from "@mui/material/styles";
// WHITE-LABEL: the sidebar header no longer bundles a Step Up asset.
// The logo comes from `state.company.branding` (hydrated by useCompanyBoot
// from /company/branding), with a neutral empty state when a tenant has
// not yet uploaded any brand assets. Never fall back to another tenant's
// logo — an empty header is the correct pre-hydration UI.
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import { useStyles } from "../../theme/appStyles";
import {
  Avatar,
  Badge,
  Collapse,
  Grid,
  ListItemButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo, useState } from "react";
import { useThemeContext } from "./ThemeContext";
import Notification from "../../Components/Popup/notification";
import { MdDarkMode } from "react-icons/md";
import Profile from "../../Components/Popup/profile";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Privileges } from "../../Core/Enum/enum";
import HelpButton from "../../Components/Help/HelpButton";
import useUser from "../../Core/hook/useUser";
import {
  useGetTeamQuery,
  useGetUserQuery,
  useGetRequestNotificationQuery,
  useGetallStudentQuery,
} from "../../Api/attoDeskApi";
import {
  IStudent,
  ITeam,
  IUserInfo,
} from "../../Api/Interface/api.interface";
import useApp from "../../utils/useApp";
import useIsMobile from "../../Core/hook/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { setCompanyInfo } from "../../Store/companySlice";
import { RootState } from "../../Store";
import { toProperCase } from "../../Components/Validation/toProperCase";
import { MdOutlineNotificationsNone } from "react-icons/md";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { PiStudentBold } from "react-icons/pi";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import dayjs from "dayjs";

// ── Menu Icons ──
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

// ── Layout Constants ──
// 296 (not 264) — bumped for long-label headroom. At 264 px the
// submenu labels "Regular Class Schedule Report" and "Generate Classes
// from Backup" pushed past the button's 12 px right padding into the
// drawer edge, and the shared "…Report" siblings truncated the
// disambiguating tail rather than the redundant "Report" head. 32 extra
// pixels ≈ 4 characters, which clears the current label set. The
// ellipsis + tooltip in saas-overrides.css stay in as the dormant
// safety net for whatever label lands here next.
const drawerWidth = 296;
const headerHeight = 64;
const closedDrawerWidth = 72;

// ── Types ──
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

type SubmenuLink = {
  topBarTitle?: string;
  title: string;
  path: string;
  newPage?: boolean;
  icon: React.ReactElement;
  privilege: string;
  // Optional path prefix that ALSO counts as "on this submenu" for the
  // active-state effect. Use when the link target is deeper than the section
  // (e.g. Session Data's link goes to /session-data/create but /session-data/*
  // should also highlight it). See sidebar spec Section 3.
  matchPrefix?: string;
  separator?: false;
  // Portal separation. Same semantics as MenuItem.adminOnly — the
  // submenu link is hidden from everyone but a System_Admin, even
  // if their role holds the underlying privilege.
  adminOnly?: boolean;
  // Mirror of `adminOnly` for Staff Portal items: shown only when the
  // user's canonical role is "Staff" AND they are NOT a System_Admin.
  // Prevents admins (who often hold Assessment_View / Student_View /
  // Message_View by default) from seeing "My Assessments" etc. in
  // their sidebar and being routed into /staff-portal/* pages.
  staffOnly?: boolean;
};
// Batch 61 §1 — inline section header inside a submenu list. Used to group
// related items (e.g. ABA Therapy → SETTINGS → Prompt Levels / Workflows /
// Treatment Areas / Program Templates) without introducing a second top-level
// menu section. Renderer treats separator items as non-interactive labels.
type SubmenuSeparator = {
  separator: true;
  label: string;
  privilege?: string; // when set, header is hidden if the user lacks it
};
type SubmenuItem = SubmenuLink | SubmenuSeparator;

type MenuItem = {
  topBarTitle: string;
  title: string;
  icon: React.ReactElement;
  path: string;
  newPage?: boolean;
  submenuOpen?: boolean;
  submenus?: SubmenuItem[];
  privilege: string;
  // Portal separation, Phase 1. When true, the item is hidden from
  // every user who is NOT a System_Admin — even if their role happens
  // to hold the underlying privilege. Backend still enforces its own
  // permission middleware; this is the UX half of the same rule.
  adminOnly?: boolean;
  // Mirror of `adminOnly` — see SubmenuLink.staffOnly. Hidden from
  // admins so admin sidebars never link to /staff-portal/* pages.
  staffOnly?: boolean;
};

type MenuSection = {
  sectionLabel: string;
  menuList?: MenuItem[];
};

// ── Menu Configuration ──
const MainMenuList: MenuSection[] = [
  {
    sectionLabel: "MAIN",
    menuList: [
      {
        // Admin dashboard — school-wide. Explicitly `adminOnly` so
        // Staff users see the Staff Portal Dashboard entry below
        // instead of being routed into admin surfaces.
        title: "Dashboard",
        topBarTitle: "Welcome!",
        icon: <DashboardOutlinedIcon />,
        path: "/dashboard",
        privilege: Privileges.DashboardView,
        adminOnly: true,
      },
      {
        // Staff Portal home — same page as the login redirect. Shown
        // FIRST in the staff sidebar (spec §5 / Phase 5) so the
        // labelled main tab reads "Dashboard", not "Today".
        //
        // Gate is StudentView (not DashboardView) so live Staff users
        // whose role was seeded before Dashboard_View was added to the
        // Staff canonical set do not silently lose the item. staffOnly
        // still hides it from admins. The endpoint behind it is
        // Auth::user()->staffId-scoped, so widening this gate cannot
        // leak another staff's data.
        title: "Dashboard",
        topBarTitle: "Dashboard",
        icon: <DashboardOutlinedIcon />,
        path: "/staff-portal",
        privilege: Privileges.StudentView,
        staffOnly: true,
      },
      {
        // Staff Portal — My Profile (view-only self-service page).
        // Placed right after Dashboard in the MAIN section so staff
        // can reach it before all the workspace items. Backend
        // /api/staff-portal/profile is Auth::user()->staffId-scoped;
        // there is no IDOR surface. Gate is StudentView (the seeded
        // Staff role's baseline) so no live staff loses it, mirroring
        // the Dashboard item above. staffOnly hides it from admins,
        // who have the school-wide Staff Management surface instead.
        title: "My Profile",
        topBarTitle: "My Profile",
        icon: <PersonOutlineIcon />,
        path: "/staff-portal/profile",
        privilege: Privileges.StudentView,
        staffOnly: true,
      },
      {
        // Aug 2026 — Staff Portal Phase 5. `/staff-portal` is the
        // canonical staff Dashboard (item above). The legacy `/today`
        // matrix stays available for admins/managers who use its
        // school-wide grid, but must NOT appear in the staff sidebar
        // alongside "Dashboard" or the two overlap into a confusing
        // "two homes" navigation. Staff reach the day view via
        // Dashboard's Coming Up + Quick Actions.
        title: "Today",
        topBarTitle: "Today",
        icon: <TodayOutlinedIcon />,
        path: "/today",
        privilege: Privileges.ClassScheduleView,
        adminOnly: true,
      },
      {
        // School-wide Students management (admins only). Phase 3
        // introduces a staff-scoped "My Students" surface below;
        // this item stays for admins who need the full directory.
        topBarTitle: "Student List",
        title: "Students",
        icon: <SchoolOutlinedIcon />,
        path: "/student/list",
        privilege: Privileges.StudentView,
        adminOnly: true,
      },
      {
        // Staff Portal Phase 3a — teacher-scoped student list. Hits
        // /api/staff-portal/my-students which uses Auth::user()->staffId
        // server-side. Non-admin employees see this instead of the
        // school-wide Students item above.
        topBarTitle: "My Students",
        title: "My Students",
        icon: <SchoolOutlinedIcon />,
        path: "/staff-portal/students",
        privilege: Privileges.StudentView,
        staffOnly: true,
      },
      {
        // Staff Portal Phase 3b — teacher-scoped assessments. Row-
        // level scope enforced by StaffTeachingScopeService::canAccessAssessment
        // (assessments.staff_id === user.staffId).
        topBarTitle: "My Assessments",
        title: "My Assessments",
        icon: <SchoolOutlinedIcon />,
        path: "/staff-portal/assessments",
        privilege: "Assessment_View",
        staffOnly: true,
      },
      {
        // Staff Portal — Create Class (2026-08-25). Auth-derived: the
        // page renders a locked staff identity and hydrates Subjects &
        // Grades from the acting staff's canonical teaching
        // assignments. Backend classesAssignController::store
        // overwrites staff_id from auth for non-admin callers and
        // rejects any grade-subject pair outside the staff's teaching
        // scope, so this menu entry is safe even if the frontend is
        // bypassed.
        topBarTitle: "Create Class",
        title: "Create Class",
        icon: <SchoolOutlinedIcon />,
        path: "/staff-portal/classes/create",
        privilege: "Classes_Create",
        staffOnly: true,
      },
      {
        // Staff Portal — Attendance surfaced as a top-level MAIN item
        // (was previously buried under ACADEMICS → Classes, which
        // teachers rarely opened). Points at the SAME `/attendance`
        // route the admin nested item uses; the shared page is
        // permission-aware (attendance.access middleware admits admin
        // RBAC OR staff ownership of the class occurrence). The old
        // nested Attendance is now `adminOnly` so it stops appearing
        // twice for staff.
        topBarTitle: "Mark Attendance",
        title: "Attendance",
        icon: <AssignmentTurnedInOutlinedIcon />,
        path: "/attendance",
        privilege: Privileges.AttendanceView,
        staffOnly: true,
      },
      // My Assignments removed from the Staff Portal sidebar (per
      // product ask). Grading + academic-work capture for staff now
      // funnels through "My Assessments" alone. The Assignments
      // model, admin wizard, parent-portal listing, and student
      // submission history remain intact — this is a Staff Portal
      // navigation change, not a data removal. The old URL
      // /staff-portal/assignments is redirected to
      // /staff-portal/assessments in Routes.tsx so any bookmark still
      // lands on a working page.
    ],
  },
  {
    sectionLabel: "ACADEMICS",
    menuList: [      
      {
        topBarTitle: "Classes List",
        title: "Classes",
        icon: <ClassOutlinedIcon />,
        // Parent-node link points at Class Sessions since "All Classes" is
        // gone — the two overlapping calendars have been consolidated into
        // Class Sessions (dated occurrences) + Class Schedules (recurring
        // patterns). See Routes.tsx for the /classes/list redirect.
        path: "/class-schedule/list",
        submenuOpen: true,
        privilege: Privileges.ClassScheduleView,
        submenus: [
          { topBarTitle: "Class Sessions", title: "Class Sessions", icon: <FiberManualRecordIcon />, path: "/class-schedule/list", privilege: Privileges.ClassScheduleView },
          { topBarTitle: "Class Schedules", title: "Class Schedules", icon: <FiberManualRecordIcon />, path: "/class-schedules", privilege: Privileges.ClassScheduleView },
          { topBarTitle: "Pending Makeups", title: "Pending Makeups", icon: <FiberManualRecordIcon />, path: "/class-schedules/pending-makeups", privilege: Privileges.ClassScheduleView },
          // The nested Classes → Parent Requests entry is admin-only
          // because staff already reach a teaching-scoped variant via
          // COMMUNICATION → Parent Requests (/staff-portal/parent-
          // requests, gated by ParentRequest_View). Rendering both for
          // staff produced two sidebar shortcuts to conceptually the
          // same operational surface. This page (/classes/parent-
          // requests, ClassSchedule_Edit) still exists for admin
          // school-wide review.
          { topBarTitle: "Parent Requests", title: "Parent Requests", icon: <FiberManualRecordIcon />, path: "/classes/parent-requests", privilege: Privileges.ClassScheduleView, adminOnly: true },
          // Attendance nested under Classes is admin-only now. Staff
          // reach the same page via the top-level MAIN → Attendance
          // item added under My Assessments (see above); rendering it
          // in both places for staff would be duplicate navigation.
          { topBarTitle: "Mark Attendance", title: "Attendance", icon: <FiberManualRecordIcon />, path: "/attendance", privilege: Privileges.AttendanceView, adminOnly: true },
          // Admin-wide Assessments dashboard. Phase 3b introduces
          // a staff-scoped My Assessments item below; this stays
          // for admins (same `adminOnly` pattern as Finance/Parents/PPM).
          { topBarTitle: "Assessments", title: "Assessments", icon: <FiberManualRecordIcon />, path: "/assessments", privilege: Privileges.AttendanceView, adminOnly: true },
        ],
      },
      {
        topBarTitle: "Course List",
        title: "Courses",
        icon: <MenuBookOutlinedIcon />,
        path: "/course/list",
        privilege: Privileges.CourseView,
      },
      {
        title: "ABA Therapy",
        topBarTitle: "Programs",
        icon: <AutoStoriesOutlinedIcon />,
        path: "/aba-therapy/programs",
        submenuOpen: true,
        privilege: Privileges.ProgramLibraryView,
        submenus: [
          // Batch 32 §1 — sidebar and breadcrumb now agree: the whole module
          // lives under /aba-therapy/*. matchPrefix keeps the correct child
          // highlighted on every editor/detail child route (new/:id/edit/etc).
          // Old prefixes (/program-library, /session-data, /settings/workflows,
          // /settings/prompt-templates) also match so a user landing via a
          // legacy bookmark still sees the correct row highlighted for the
          // one render-frame before the redirect fires.
          { topBarTitle: "Programs",           title: "Programs",           icon: <FiberManualRecordIcon />, path: "/aba-therapy/programs",           matchPrefix: "/aba-therapy/programs",           privilege: Privileges.ProgramLibraryView },
          { topBarTitle: "Session Data",       title: "Session Data",       icon: <FiberManualRecordIcon />, path: "/aba-therapy/sessions",           matchPrefix: "/aba-therapy/sessions",           privilege: Privileges.SessionDataView },
          { topBarTitle: "Mastery Approvals",  title: "Mastery Approvals",  icon: <FiberManualRecordIcon />, path: "/aba-therapy/mastery-approvals",  matchPrefix: "/aba-therapy/mastery-approvals",  privilege: Privileges.WorkflowView },
          // Batch 61 §1 — Settings sub-group. Matches the client's
          // vocabulary (Prompt Levels, Workflows) and adds two new
          // supervisor surfaces (Treatment Areas, Program Templates).
          // Tags and Program Data Fields are deferred; they get added
          // when their backends land.
          { separator: true, label: "SETTINGS", privilege: Privileges.ProgramLibraryView },
          { topBarTitle: "Treatment Areas",    title: "Treatment Areas",    icon: <FiberManualRecordIcon />, path: "/aba-therapy/settings/treatment-areas", matchPrefix: "/aba-therapy/settings/treatment-areas", privilege: Privileges.ProgramLibraryView },
          { topBarTitle: "Prompt Levels",      title: "Prompt Levels",      icon: <FiberManualRecordIcon />, path: "/aba-therapy/settings/prompt-levels",   matchPrefix: "/aba-therapy/settings/prompt-levels",   privilege: Privileges.PromptTemplateView },
          { topBarTitle: "Workflows",          title: "Workflows",          icon: <FiberManualRecordIcon />, path: "/aba-therapy/settings/workflows",       matchPrefix: "/aba-therapy/settings/workflows",       privilege: Privileges.WorkflowView },
          { topBarTitle: "Program Templates",  title: "Program Templates",  icon: <FiberManualRecordIcon />, path: "/aba-therapy/settings/templates",       matchPrefix: "/aba-therapy/settings/templates",       privilege: Privileges.ProgramLibraryView },
        ],
      }
    ],
  },

  {
    sectionLabel: "REPORTS",
    menuList: [      
      {
        topBarTitle: "Student Subjects List",
        title: "Students",
        icon: <ClassOutlinedIcon />,
        // Parent-node link points at Class Sessions since "All Classes" is
        // gone — the two overlapping calendars have been consolidated into
        // Class Sessions (dated occurrences) + Class Schedules (recurring
        // patterns). See Routes.tsx for the /classes/list redirect.
        path: "/reports/student-subjects",
        submenuOpen: true,
        privilege: Privileges.ClassScheduleView,
        submenus: [
       { topBarTitle: "Attendance Report", title: "Attendance Report", icon: <FiberManualRecordIcon />, path: "/attendance-record/report", matchPrefix: "/attendance/report", privilege: Privileges.AttendanceView },
       // Staff-only Report Cards. `staffOnly: true` keeps this out of
       // the Admin sidebar; the destination endpoint is scoped by
       // StaffTeachingScopeService so an admin visiting the URL
       // directly still functions but doesn't see the menu item here.
       { topBarTitle: "Report Cards", title: "Report Cards", icon: <FiberManualRecordIcon />, path: "/staff-portal/reports/report-cards", matchPrefix: "/staff-portal/reports/report-cards", privilege: Privileges.AttendanceView, staffOnly: true },
          { topBarTitle: "Student Grade & Subjects", title: "Student Subjects Report", icon: <FiberManualRecordIcon />, path: "/reports/student-subjects", privilege: Privileges.AcademicReportStudentSubjects },         
          { topBarTitle: "Student Class Assignments", title: "Student Classes Report", icon: <FiberManualRecordIcon />, path: "/reports/student-classes", privilege: Privileges.AcademicReportStudentClasses },
          { topBarTitle: "Student Timetable", title: "Student Timetable Report", icon: <FiberManualRecordIcon />, path: "/reports/student-timetable", privilege: Privileges.AcademicReportStudentTimetable },
        ],
      },
        {
        topBarTitle: "Staffs",
        title: "Staffs",
        icon: <PeopleAltOutlinedIcon />,
        path: "/reports/staff-capacity",
        submenuOpen: true,
        privilege: Privileges.PaymentView,
        submenus: [
          { topBarTitle: "Regular Class Schedule", title: "Regular Class Schedule Report", icon: <FiberManualRecordIcon />, path: "/reports/regular-class-schedule", privilege: Privileges.AcademicReportStudentClasses },
          { topBarTitle: "Staff Class Capacity", title: "Staff Capacity Report", icon: <FiberManualRecordIcon />, path: "/reports/staff-capacity", privilege: Privileges.AcademicReportStaffCapacity },
          { topBarTitle: "Staff Timetable", title: "Staff Timetable Report", icon: <FiberManualRecordIcon />, path: "/reports/staff-timetable", privilege: Privileges.AcademicReportStaffTimetable },
        ],
      },
    ],
  },

  {
    sectionLabel: "MANAGEMENT",
    menuList: [
      {
        title: "Staff",
        topBarTitle: "Staff",
        icon: <BadgeOutlinedIcon />,
        path: "/team/list",
        submenuOpen: true,
        privilege: Privileges.StaffView,
        submenus: [
          { title: "Staff List", topBarTitle: "Staff", icon: <FiberManualRecordIcon />, path: "/team/list", matchPrefix: "/team-history", privilege: Privileges.StaffView },
          { title: "Staff Dashboard", topBarTitle: "Staff Dashboard", icon: <FiberManualRecordIcon />, path: "/staff-dashboard", privilege: Privileges.StaffView },
          { title: "Clock In/Out", topBarTitle: "Clock In/Out List", icon: <FiberManualRecordIcon />, path: "/schedule/list", privilege: Privileges.ScheduleView },
          // "Schedule & Reschedule List" (path: /schedule/list/calender/one-day)
          // removed 2026-08-25 — that screen was the pre-Class-Sessions
          // legacy scheduler and duplicated functionality now covered by
          // ACADEMICS → Classes → Class Sessions (/class-schedule/list).
          // The old route now redirects to /class-schedule/list so any
          // remaining bookmark / deep link still lands somewhere valid.
          { title: "Payroll", topBarTitle: "Payroll List", icon: <FiberManualRecordIcon />, path: "/payroll/list", privilege: Privileges.PayrollView },
        ],
      },
      {
        topBarTitle: "Finance",
        title: "Finance",
        icon: <AccountBalanceWalletOutlinedIcon />,
        path: "/finance",
        submenuOpen: true,
        privilege: Privileges.PaymentView,
        adminOnly: true,
        submenus: [
          { topBarTitle: "Finance Dashboard", title: "Overview", icon: <FiberManualRecordIcon />, path: "/finance", privilege: Privileges.PaymentView },
          { topBarTitle: "Invoices", title: "Invoices", icon: <FiberManualRecordIcon />, path: "/finance/invoices", privilege: Privileges.InvoiceView },
          { topBarTitle: "Transactions", title: "Transactions", icon: <FiberManualRecordIcon />, path: "/finance/transactions", privilege: Privileges.PaymentView },
          { topBarTitle: "Expenses", title: "Expenses", icon: <FiberManualRecordIcon />, path: "/finance/expenses", privilege: Privileges.PaymentView },
          { topBarTitle: "Reports", title: "Reports", icon: <FiberManualRecordIcon />, path: "/finance/reports", privilege: Privileges.PaymentView },
          { topBarTitle: "Payment Reconciliation", title: "Reconciliation", icon: <FiberManualRecordIcon />, path: "/finance/payments/reconciliation", privilege: Privileges.PaymentView },
        ],
      },
      {
        topBarTitle: "Parents",
        title: "Parents",
        icon: <PeopleAltOutlinedIcon />,
        path: "/parents/list",
        privilege: Privileges.ParentView,
        adminOnly: true,
      },
    ],
  },
  {
    // Lead System + Email Hub, grouped. Pages self-gate via usePermission;
    // gated here on DashboardView (Leads) / EmailView (Email Hub) so staff see
    // them. 🟨 Swap to dedicated Lead_View / EmailHub_View privileges when defined.
    sectionLabel: "LEADS & EMAIL",
    menuList: [
      {
        topBarTitle: "Leads",
        title: "Leads",
        icon: <LeaderboardOutlinedIcon />,
        path: "/leads/inquiries",
        submenuOpen: true,
        privilege: Privileges.DashboardView,
        submenus: [
          { topBarTitle: "All Leads", title: "All Leads", icon: <FiberManualRecordIcon />, path: "/leads/inquiries", privilege: Privileges.DashboardView },
          { topBarTitle: "Pipeline", title: "Pipeline", icon: <FiberManualRecordIcon />, path: "/leads/pipeline", privilege: Privileges.DashboardView },
          { topBarTitle: "Calendar", title: "Calendar", icon: <FiberManualRecordIcon />, path: "/leads/calendar", privilege: Privileges.DashboardView },
          { topBarTitle: "Lead Dashboard", title: "Dashboard", icon: <FiberManualRecordIcon />, path: "/leads/dashboard", privilege: Privileges.DashboardView },
          { topBarTitle: "My Tasks", title: "My Tasks", icon: <FiberManualRecordIcon />, path: "/tasks", privilege: Privileges.DashboardView },
        ],
      },
      {
        topBarTitle: "Email Hub",
        title: "Email Hub",
        icon: <EmailOutlinedIcon />,
        path: "/email-hub",
        submenuOpen: true,
        privilege: Privileges.EmailView,
        submenus: [
          { topBarTitle: "Inbox", title: "Inbox", icon: <FiberManualRecordIcon />, path: "/email-hub", privilege: Privileges.EmailView },
          { topBarTitle: "Email Pipeline", title: "Pipeline", icon: <FiberManualRecordIcon />, path: "/email-hub/pipeline", privilege: Privileges.EmailView },
          { topBarTitle: "Email Dashboard", title: "Dashboard", icon: <FiberManualRecordIcon />, path: "/email-hub/dashboard", privilege: Privileges.EmailView },
          { topBarTitle: "Email Settings", title: "Settings", icon: <FiberManualRecordIcon />, path: "/email-hub/settings", privilege: Privileges.EmailView },
        ],
      },
    ],
  },
  {
    sectionLabel: "COMMUNICATION",
    menuList: [
      {
        // Admin school-wide messaging surface. Staff Portal Phase 3c
        // (2026_08_17) added a self-scoped staff surface below —
        // admins keep the organization-wide inbox here.
        topBarTitle: "Messages",
        title: "Messages",
        icon: <ChatOutlinedIcon />,
        path: "/message/list",
        privilege: Privileges.MessageView,
        adminOnly: true,
      },
      {
        // Staff Portal Phase 3c — participant-scoped portal messages.
        // Row filter enforced by StaffTeachingScopeService; new sends
        // require current teaching scope + linked parent.
        topBarTitle: "Messages",
        title: "Messages",
        icon: <ChatOutlinedIcon />,
        path: "/staff-portal/messages",
        privilege: Privileges.MessageView,
        staffOnly: true,
      },
      {
        // Staff-owned reusable message templates that populate the
        // New Message composer chips. System defaults are seeded;
        // staff can Customize (creates an owned override) or add
        // their own custom templates. Gated by Message_View.
        topBarTitle: "Message Templates",
        title: "Message Templates",
        icon: <EditNoteOutlinedIcon />,
        path: "/staff-portal/message-templates",
        privilege: Privileges.MessageView,
        staffOnly: true,
      },
      {
        // Communication Phase 2 — Newsletters & Campaigns workspace.
        // Server-side permissions gate every mutation; this hides the
        // surface from users without Campaign_View. The list route
        // owns everything under /communication/newsletters — the
        // longest-prefix matcher on the sidebar (Menu.tsx:1447) keeps
        // wizard + detail subroutes highlighted here.
        topBarTitle: "Newsletters",
        title: "Newsletters",
        icon: <EditNoteOutlinedIcon />,
        path: "/communication/newsletters",
        privilege: Privileges.CampaignView,
        adminOnly: true,
      },
      {
        // Communication Phase 2 (Pass D) — central history of every
        // outbound Email + SMS from the modern audit tables. Legacy
        // Mail::send-only flows (60 sites: password reset, invoices,
        // reminders) are intentionally out of scope — they don't write
        // to `emails` and can't be surfaced without rewriting them.
        topBarTitle: "History",
        title: "History",
        icon: <EditNoteOutlinedIcon />,
        path: "/communication/history",
        privilege: Privileges.CommunicationHistoryView,
        adminOnly: true,
      },
      {
        // Direct Message composer (Pass F2) — one-off email / SMS to a
        // single recipient via Microsoft Graph / INTALK. Distinct from
        // the legacy in-app /message/list chat feature, which stays in
        // its own sidebar section under Message_View.
        topBarTitle: "Direct Message",
        title: "Direct Message",
        icon: <EditNoteOutlinedIcon />,
        path: "/communication/direct-message",
        privilege: Privileges.DirectCommunicationView,
        adminOnly: true,
      },
      {
        // Communication Phase 2 (Pass E §9) — primary Templates
        // destination. The original Settings sidebar entry has been
        // removed but the /settings/communication-templates route
        // remains registered as an alias so existing bookmarks
        // continue to work.
        topBarTitle: "Communication Templates",
        title: "Templates",
        icon: <EditNoteOutlinedIcon />,
        path: "/communication/templates",
        privilege: Privileges.SettingView,
        adminOnly: true,
      },
      {
        // Staff Portal Phase 3c — parent requests for the acting
        // staff's own occurrences. Gated by ParentRequest_View
        // (route middleware); handle actions gated by ParentRequest_Manage.
        // Deliberately NOT reusing broad ClassSchedule_Edit.
        topBarTitle: "Parent Requests",
        title: "Parent Requests",
        icon: <AssignmentLateOutlinedIcon />,
        path: "/staff-portal/parent-requests",
        privilege: "ParentRequest_View",
        staffOnly: true,
      },
      // "Parent Portal Management" sidebar entry removed per client
      // request. Parent account + portal-access controls (Enable /
      // Suspend / Reset Password / Send Login Instructions / linked
      // children) remain available in the canonical Parents page
      // (/parents/list). The `/parent-portal-management` route now
      // redirects to /parents/list for legacy bookmarks. Backend
      // endpoints, permissions, and the actual Parent Portal
      // (/parent-portal) are untouched.
      // Email Hub moved to the "LEADS & EMAIL" section above.
    ],
  },
  {
    sectionLabel: "SYSTEM",
    menuList: [
      {
        title: "Settings",
        topBarTitle: "Settings",
        icon: <SettingsOutlinedIcon />,
        path: "/looping",
        submenuOpen: true,
        privilege: Privileges.SettingView,
        // Operational / day-to-day configuration only.
        // Admin-only items (Company / Theme Color / Users / User Roles /
        // Privileges) moved to the ADMIN SETTINGS section below.
        // Routes + permissions unchanged.
        submenus: [
          { topBarTitle: "Locations", title: "Locations", icon: <FiberManualRecordIcon />, path: "/location/list", privilege: Privileges.StudentView },
          { topBarTitle: "Grades and Subjects", title: "Grades & Subjects", icon: <FiberManualRecordIcon />, path: "/class/subject/list", privilege: Privileges.StudentView },
          { topBarTitle: "Assessment Focus Areas", title: "Focus Areas", icon: <FiberManualRecordIcon />, path: "/assessment/focus-areas/list", privilege: Privileges.SettingView },
          { topBarTitle: "Email", title: "Email", icon: <FiberManualRecordIcon />, path: "/email/setting/list", privilege: Privileges.EmailView },
          // Legacy data migration tool — writes production class data; gated to admins with ClassBackupImport_View.
          { topBarTitle: "Class Backup Import", title: "Generate Classes from Backup", icon: <FiberManualRecordIcon />, path: "/settings/class-backup-import", privilege: Privileges.ClassBackupImportView },
          { topBarTitle: "Makeup Classes Import", title: "Makeup Classes Import", icon: <FiberManualRecordIcon />, path: "/settings/class-backup-import/makeups", privilege: Privileges.ClassBackupImportView },
          // Attendance Messaging Phase 2 — tenant/global message
          // templates. adminOnly so it hides for staff even if a role
          // change temporarily grants CompanyView. Backend re-enforces
          // System_Admin on every write.
          { topBarTitle: "Message Templates", title: "Message Templates", icon: <FiberManualRecordIcon />, path: "/settings/message-templates", privilege: Privileges.SettingView, adminOnly: true },
          // Communication Templates moved to the COMMUNICATION section
          // (Pass E §9). Route /settings/communication-templates
          // remains registered in Routes.tsx as an alias for legacy
          // bookmarks — nothing calls out to it from the sidebar.
        ],
      },
      {
        title: "Additional",
        topBarTitle: "Position/Department",
        icon: <TuneOutlinedIcon />,
        path: "/sc/othe",
        submenuOpen: true,
        privilege: Privileges.DepartmentView,
        submenus: [
          { topBarTitle: "Department List", title: "Departments", icon: <FiberManualRecordIcon />, path: "/department/list", privilege: Privileges.DepartmentView },
          { topBarTitle: "Position List", title: "Positions", icon: <FiberManualRecordIcon />, path: "/position/list", privilege: Privileges.PositionView },
        ],
      },
    ],
  },
  {
    // ADMIN SETTINGS — items that manage tenant identity, access control
    // and visual theme. Separated from the day-to-day "SYSTEM > Settings"
    // group so admin actions are one click deeper from operational ones.
    // Routes and privilege gates are UNCHANGED — this is presentation only.
    sectionLabel: "ADMIN SETTINGS",
    menuList: [
      {
        title: "Admin",
        topBarTitle: "Admin Settings",
        icon: <AdminPanelSettingsOutlinedIcon />,
        path: "/admin-settings",
        submenuOpen: true,
        // Any of the child privileges opens the group; each child re-checks
        // its own privilege as before. Company + Theme Color use their own
        // gates; Users/Roles/Privileges all use UserView.
        privilege: Privileges.UserView,
        submenus: [
          { topBarTitle: "Company",     title: "Company",     icon: <FiberManualRecordIcon />, path: "/setting",               privilege: Privileges.SettingView },
          { topBarTitle: "Theme Color", title: "Theme Color", icon: <FiberManualRecordIcon />, path: "/theme-color",           privilege: Privileges.ThemeView   },
          { topBarTitle: "User",        title: "Users",       icon: <FiberManualRecordIcon />, path: "/user/list",             privilege: Privileges.UserView    },
          { topBarTitle: "User Role",   title: "User Roles",  icon: <FiberManualRecordIcon />, path: "/user/role/list",        privilege: Privileges.UserView    },
          { topBarTitle: "Privilege",   title: "Privileges",  icon: <FiberManualRecordIcon />, path: "/user/privilege/list",   privilege: Privileges.UserView    },
        ],
      },
    ],
  },
  {
    sectionLabel: "HELP",
    menuList: [
      {
        title: "Help Center",
        topBarTitle: "Help Center",
        icon: <ArticleOutlinedIcon />,
        path: "/help-center",
        privilege: Privileges.HelpArticleView,
      },
      {
        title: "Manage Documentation",
        topBarTitle: "Manage Documentation",
        icon: <ArticleOutlinedIcon />,
        path: "/help-center/manage",
        privilege: Privileges.HelpArticleEdit,
      },
      {
        title: "Project Reference",
        topBarTitle: "Project Reference",
        icon: <ArticleOutlinedIcon />,
        path: "/documentation",
        // Dashboard_View is held by every staff role → visible to all.
        privilege: Privileges.DashboardView,
      },
    ],
  },
];

// ── Styled Components ──
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: headerHeight,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: "none",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backdropFilter: "blur(8px)",
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    paddingLeft: 0,
  }),
  ...(!open && {
    marginLeft: 0,
    width: "100%",
    paddingLeft: `${closedDrawerWidth}px`,
    [theme.breakpoints.down("md")]: {
      paddingLeft: "0px",
    },
  }),
}));

const AppBarFullScreen = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: headerHeight,
  boxShadow: "none",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backdropFilter: "blur(8px)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    paddingLeft: 0,
  }),
  ...(!open && {
    marginLeft: 0,
    width: "100%",
    paddingLeft: "5px",
    [theme.breakpoints.down("md")]: {
      paddingLeft: "0px",
    },
  }),
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `${closedDrawerWidth}px`,
});

const IsNotMobileDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      borderRight: "none",
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      borderRight: "none",
    },
  }),
}));

const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    borderRight: "none",
  },
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: headerHeight,
  padding: theme.spacing(0, 2),
  backgroundColor: theme.palette.topbarButton.drawerHeader,
  flexShrink: 0,
  justifyContent: "space-between",
  borderBottom: `1px solid ${alpha("#fff", 0.06)}`,
}));

const DrawerContent = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.mainMenuNav.mainNavBg,
  overflowY: "auto",
  flexGrow: 1,
  padding: "8px",
  "&::-webkit-scrollbar": {
    width: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha("#fff", 0.1),
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: alpha("#fff", 0.2),
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
}));

const PaddingBody = styled("div")(({ theme }) => ({
  height: headerHeight,
}));

// ── Reusable Sidebar Menu Content ──
interface SidebarMenuContentProps {
  open: boolean;
  expandMenu: string;
  currentMenuName: string;
  selectedSubmenu: string;
  hoveredMenuName: string;
  hoveredSubmenu: string;
  setHoveredMenuName: (v: string) => void;
  setHoveredSubmenu: (v: string) => void;
  handleMainMenuItemClick: (ml: MenuItem) => void;
  handleSubmenuItemClicked: (s: SubmenuItem) => void;
  userHasRight: (p: string) => boolean;
  isParentUser: boolean;
  isSystemAdmin: boolean;
  isStaffRole: boolean;
}

const SidebarMenuContent: React.FC<SidebarMenuContentProps> = ({
  open,
  expandMenu,
  currentMenuName,
  selectedSubmenu,
  hoveredMenuName,
  hoveredSubmenu,
  setHoveredMenuName,
  setHoveredSubmenu,
  handleMainMenuItemClick,
  handleSubmenuItemClicked,
  userHasRight,
  isParentUser,
  isSystemAdmin,
  isStaffRole,
}) => {
  const theme = useTheme();

  if (isParentUser) return null;

  const isActive = (path: string) =>
    currentMenuName === path || selectedSubmenu?.startsWith(path);

  const menuTextColor = theme.palette.topbarButton.drawerMenuText;
  const hoverColor = theme.palette.mainMenuNav.hoverColorNavBg;
  const activeColor = "#fff";

  return (
    <>
      {MainMenuList.map((section, sIdx) => {
        const visibleItems = section.menuList?.filter((m) => {
          // Portal separation. Two symmetric gates:
          //   `adminOnly` — admin-side surfaces (Finance, Parents, PPM,
          //     Settings, etc.). Hidden FOR the seeded Staff role so
          //     clicking them from a Staff account can't land on a
          //     403; shown for everyone else who holds the privilege
          //     (System Admin, custom admin-side roles like
          //     Receptionist, Finance, etc.). Parents never reach this
          //     filter — they render in ParentPortalLayout upstream.
          //   `staffOnly` — hide FOR System Admin AND for non-staff
          //     (blocks admins from being routed to /staff-portal/*
          //     pages just because they hold the underlying privilege).
          // Backend still gates every endpoint; this is the UX half.
          if (!userHasRight(m.privilege)) return false;
          if (m.adminOnly && isStaffRole) return false;
          if (m.staffOnly && (isSystemAdmin || !isStaffRole)) return false;
          return true;
        }) || [];
        if (visibleItems.length === 0) return null;

        return (
          <React.Fragment key={sIdx}>
            {/* Section Label */}
            {open && section.sectionLabel && (
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: alpha(menuTextColor, 0.35),
                  px: 1.5,
                  pt: sIdx === 0 ? 0.5 : 2,
                  pb: 0.75,
                  userSelect: "none",
                }}
              >
                {section.sectionLabel}
              </Typography>
            )}
            {!open && sIdx > 0 && (
              <Box
                sx={{
                  mx: 1.5,
                  my: 1,
                  borderTop: `1px solid ${alpha("#fff", 0.06)}`,
                }}
              />
            )}

            {/* Menu Items */}
            {visibleItems.map((menuItem) => {
              const active = isActive(menuItem.path);
              const hovered = hoveredMenuName === menuItem.path;
              const hasSubmenu = menuItem.submenus && menuItem.submenus.length > 0;
              const expanded = expandMenu === menuItem.title;

              return (
                <Box key={menuItem.title} sx={{ mb: 0.25 }}>
                  {/* Main Menu Item */}
                  <ListItemButton
                    component={hasSubmenu ? "div" : Link}
                    to={hasSubmenu ? undefined : menuItem.path}
                    target="_self"
                    onClick={() => handleMainMenuItemClick(menuItem)}
                    onMouseEnter={() => setHoveredMenuName(menuItem.path)}
                    onMouseLeave={() => setHoveredMenuName("")}
                    sx={{
                      minHeight: 42,
                      borderRadius: "10px",
                      px: open ? 1.5 : 1,
                      py: 0.75,
                      mx: open ? 0 : 0.5,
                      justifyContent: open ? "flex-start" : "center",
                      bgcolor: active
                        ? alpha("#fff", 0.12)
                        : hovered
                        ? alpha("#fff", 0.06)
                        : "transparent",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        bgcolor: alpha("#fff", 0.08),
                      },
                    }}
                  >
                    {/* Icon */}
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : 0,
                        justifyContent: "center",
                        color: active ? activeColor : alpha(menuTextColor, 0.65),
                        "& svg": { fontSize: 20 },
                        transition: "color 0.15s ease",
                      }}
                    >
                      {menuItem.icon}
                    </ListItemIcon>

                    {/* Title */}
                    {open && (
                      <Tooltip title={menuItem.title} placement="right" arrow enterDelay={400}>
                        <ListItemText
                          primary={menuItem.title}
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: active ? 600 : 500,
                            color: active ? activeColor : alpha(menuTextColor, 0.75),
                            lineHeight: 1.3,
                            noWrap: true,
                          }}
                          sx={{
                            m: 0, minWidth: 0, flex: "1 1 auto", overflow: "hidden",
                            "& .MuiTypography-root": { transition: "color 0.15s ease" },
                          }}
                        />
                      </Tooltip>
                    )}

                    {/* Expand Arrow */}
                    {open && hasSubmenu && (
                      <Box
                        sx={{
                          color: alpha(menuTextColor, 0.4),
                          display: "flex",
                          alignItems: "center",
                          transition: "transform 0.2s ease",
                          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                          "& svg": { fontSize: 16 },
                        }}
                      >
                        <ExpandMoreIcon />
                      </Box>
                    )}
                  </ListItemButton>

                  {/* Submenu */}
                  {hasSubmenu && (
                    <Collapse in={expanded && open} timeout={200} unmountOnExit>
                      <Box sx={{ pl: 1.5, pr: 0.5, py: 0.25 }}>
                        {menuItem.submenus!
                          .filter((s) => {
                            // Separators may carry an optional privilege gate; if the
                            // user lacks it the header (and its following items) go
                            // untouched — items themselves are filtered by their own
                            // privilege below.
                            if ("separator" in s && s.separator) {
                              return !s.privilege || userHasRight(s.privilege);
                            }
                            const link = s as SubmenuLink;
                            if (!userHasRight(link.privilege)) return false;
                            // See the parent menu filter above for the
                            // adminOnly/staffOnly rationale — same rule.
                            if (link.adminOnly && isStaffRole) return false;
                            if (link.staffOnly && (isSystemAdmin || !isStaffRole)) return false;
                            return true;
                          })
                          .map((submenu) => {
                            // Batch 61 §1 — inline section header (non-interactive).
                            if ("separator" in submenu && submenu.separator) {
                              return (
                                <Typography
                                  key={`sep-${submenu.label}`}
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    px: 2.5,
                                    pt: 1.25,
                                    pb: 0.5,
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    letterSpacing: "0.08em",
                                    color: alpha(menuTextColor, 0.5),
                                    textTransform: "uppercase",
                                    userSelect: "none",
                                  }}
                                >
                                  {submenu.label}
                                </Typography>
                              );
                            }
                            // selectedSubmenu is set by the effect above using
                            // longest-prefix match, so an exact string compare
                            // here is enough — but we ALSO respect
                            // currentMenuName's path prefix so a page rendered
                            // before the effect runs still highlights.
                            const subActive =
                              selectedSubmenu === submenu.path ||
                              currentMenuName === submenu.path;
                            const subHovered = hoveredSubmenu === submenu.path;

                            return (
                              <ListItemButton
                                key={submenu.title}
                                component={Link}
                                to={submenu.path}
                                target="_self"
                                onClick={() => handleSubmenuItemClicked(submenu)}
                                onMouseEnter={() => setHoveredSubmenu(submenu.path)}
                                onMouseLeave={() => setHoveredSubmenu("")}
                                sx={{
                                  minHeight: 34,
                                  borderRadius: "8px",
                                  pl: 2.5,
                                  pr: 1.5,
                                  py: 0.4,
                                  mb: 0.15,
                                  position: "relative",
                                  bgcolor: subActive
                                    ? alpha("#fff", 0.08)
                                    : "transparent",
                                  "&:hover": {
                                    bgcolor: alpha("#fff", 0.05),
                                  },
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 10,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: subActive ? 6 : 4,
                                    height: subActive ? 6 : 4,
                                    borderRadius: "50%",
                                    bgcolor: subActive
                                      ? activeColor
                                      : subHovered
                                      ? alpha(menuTextColor, 0.5)
                                      : alpha(menuTextColor, 0.25),
                                    transition: "all 0.15s ease",
                                  },
                                }}
                              >
                                <Tooltip title={submenu.title} placement="right" arrow enterDelay={400}>
                                  <ListItemText
                                    primary={submenu.title}
                                    primaryTypographyProps={{
                                      fontSize: 12.5,
                                      fontWeight: subActive ? 600 : 400,
                                      color: subActive
                                        ? activeColor
                                        : subHovered
                                        ? alpha(menuTextColor, 0.9)
                                        : alpha(menuTextColor, 0.55),
                                      lineHeight: 1.3,
                                      noWrap: true,
                                    }}
                                    sx={{
                                      m: 0, minWidth: 0, flex: "1 1 auto", overflow: "hidden",
                                      "& .MuiTypography-root": { transition: "color 0.15s ease" },
                                    }}
                                  />
                                </Tooltip>
                              </ListItemButton>
                            );
                          })}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

// ── Top Bar Actions (reusable) ──
interface TopBarActionsProps {
  classes: ReturnType<typeof useStyles>;
  lastStudentId: string | null;
  lastCustomerId: string | null;
  staffLastStudentId: string | null;
  isInStaffPortal?: boolean;
  openTextStudent: boolean;
  openText: boolean;
  assignStudentMap: Map<number, string>;
  clientMap: Map<number, string>;
  setOpenTextStudent: (fn: (p: boolean) => boolean) => void;
  setOpenText: (fn: (p: boolean) => boolean) => void;
  handleClickNotificationPopUp: (e: React.MouseEvent<HTMLElement>) => void;
  toggleTheme: () => void;
  mode: string;
  handleClickProfilePopUp: (e: React.MouseEvent<HTMLElement>) => void;
  unreadCount: number;
  isMobileView?: boolean;
  navigate?: (path: string) => void;
  isStaffRole?: boolean;
}

const TopBarActions: React.FC<TopBarActionsProps> = ({
  classes,
  lastStudentId,
  lastCustomerId,
  staffLastStudentId,
  isInStaffPortal,
  openTextStudent,
  openText,
  assignStudentMap,
  clientMap,
  setOpenTextStudent,
  setOpenText,
  handleClickNotificationPopUp,
  toggleTheme,
  mode,
  handleClickProfilePopUp,
  unreadCount,
  isMobileView,
  navigate,
  isStaffRole,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      {/* Inline searched names (desktop only) */}
      {!isMobileView && (
        <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
          {lastStudentId && openTextStudent && (
            <Link to={`/student-history/${lastStudentId}`} className={classes.iconLinkPropNormal}>
              <Typography className={classes.h4QuotoTopBarSearch}>
                {assignStudentMap.get(Number(lastStudentId)) || ""}
              </Typography>
            </Link>
          )}
          {lastStudentId && openTextStudent && lastCustomerId && openText && (
            <Typography sx={{ mx: 1 }} className={classes.h4QuotoTopBarSearch}>/</Typography>
          )}
          {lastCustomerId && openText && (
            <Link to={`/team-history/${lastCustomerId}`} className={classes.iconLinkPropNormal}>
              <Typography className={classes.h4QuotoTopBarSearch}>
                {clientMap.get(Number(lastCustomerId)) || ""}
              </Typography>
            </Link>
          )}
        </Box>
      )}

      {/* Admin: existing Recently Searched Student badge (unchanged). Hidden
          while viewing a Staff Portal page so an admin's stored
          `lastStudentId` never surfaces beside the Staff badge. */}
      {!isInStaffPortal && lastStudentId && (
        <Tooltip title="Recently Searched Student">
          <IconButton
            onClick={isMobileView && navigate ? () => navigate(`/student-history/${lastStudentId}`) : () => setOpenTextStudent((prev: boolean) => !prev)}
            sx={{
              width: 38, height: 38, borderRadius: "10px",
              color: theme.palette.text.secondary,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
            }}
          >
            <PiStudentBold style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Staff: same visual + tooltip as the admin badge, using the
          staff-scoped last-student key + staff route. Render gate is the
          URL-truth Staff Portal predicate (not the strict role-name flag
          — see MiniDrawer comment). Backend enforces authorization on
          click via StaffPortalController::studentDetail. */}
      {isInStaffPortal && staffLastStudentId && (
        <Tooltip title="Recently Searched Student">
          <IconButton
            onClick={navigate ? () => navigate(`/staff-portal/students/${staffLastStudentId}`) : undefined}
            sx={{
              width: 38, height: 38, borderRadius: "10px",
              color: theme.palette.text.secondary,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
            }}
          >
            <PiStudentBold style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      {lastCustomerId && (
        <Tooltip title="Recently Searched Staff">
          <IconButton
            onClick={isMobileView && navigate ? () => navigate(`/team-history/${lastCustomerId}`) : () => setOpenText((prev: boolean) => !prev)}
            sx={{
              width: 38, height: 38, borderRadius: "10px",
              color: theme.palette.text.secondary,
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
            }}
          >
            <PersonSearchRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClickNotificationPopUp}
          sx={{
            width: 38, height: 38, borderRadius: "10px",
            color: theme.palette.text.secondary,
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
          }}
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : null}
            overlap="circular"
            color="error"
            max={99}
            classes={{ badge: unreadCount > 0 ? classes.badgePulse : undefined }}
          >
            <MdOutlineNotificationsNone
              style={{ fontSize: 20 }}
              className={unreadCount > 0 ? classes.bellActive : ""}
            />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title={mode === "light" ? "Dark Mode" : "Light Mode"}>
        <IconButton
          onClick={toggleTheme}
          sx={{
            width: 38, height: 38, borderRadius: "10px",
            color: theme.palette.text.secondary,
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
          }}
        >
          {mode === "light" ? <DarkModeOutlinedIcon sx={{ fontSize: 20 }} /> : <LightModeOutlinedIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Tooltip>
      <HelpButton />
      <Tooltip title="Profile">
        <IconButton
          onClick={handleClickProfilePopUp}
          sx={{
            width: 38, height: 38, borderRadius: "10px",
            color: theme.palette.text.secondary,
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), color: theme.palette.primary.main },
          }}
        >
          <AccountCircleRoundedIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ── Main Component ──
export default function MiniDrawer({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const classes = useStyles();
  const today = dayjs().format("MMM-DD, YYYY");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userHasRight, isParentUser, isSystemAdmin, isStaffRole } = useUser();
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileRes = useIsMobile();
  const [open, setOpen] = React.useState(true);
  const [notificationPopUp, setNotificationPopUp] = React.useState<HTMLElement | null>(null);
  const { mode, toggleTheme } = useThemeContext();
  const [profilePopUp, setProfilePopUp] = useState<HTMLElement | null>(null);
  const openProfilePopUp = Boolean(profilePopUp);
  const openNotificationPopUp = Boolean(notificationPopUp);

  const [currentMenuName, setCurrentMenuName] = React.useState("");
  const [hoveredMenuName, setHoveredMenuName] = React.useState("");
  const [expandMenu, setExpandMenu] = React.useState<string>("");
  const [topBarTitle, setTopBarTitle] = useState(today);
  const [hoveredSubmenu, setHoveredSubmenu] = React.useState("");
  const [selectedSubmenu, setSelectedSubmenu] = React.useState("");
  const { currentMenuNameFullScreen } = useApp();
  const [openText, setOpenText] = React.useState(false);
  const [openTextStudent, setOpenTextStudent] = React.useState(false);

  const { companyLogo, logowidthPrint, companyName, branding } = useSelector(
    (state: RootState) => state.company
  );
  // Sidebar logo selection:
  //   - EXPANDED: the horizontal primary logo (branding.logo → legacy
  //     companyLogo). Rendered at max 180×55 with aspect preserved.
  //   - COLLAPSED: the compact/circular mark. Prefer favicon (designed
  //     square), then secondary logo, then the primary logo scaled into
  //     a 34px cell. This is the same priority chain used by the loader
  //     so tab-icon, loader, and collapsed-sidebar share one identity.
  const primaryLogoSrc = (branding?.logo || companyLogo || "").trim();
  const compactLogoSrc = (branding?.favicon || primaryLogoSrc).trim();

  const { data, isError } = useGetUserQuery();
  const { data: allclientData } = useGetTeamQuery({ searchText: "", id: "" });
  const { data: noificationData } = useGetRequestNotificationQuery(
    { id: "" },
    { pollingInterval: 120000 }
  );
  // Only used to resolve the single id in `lastStudentId` (top-bar inline
  // breadcrumb). Fetching the full roster on every page load — on the
  // single-threaded `php artisan serve` dev stack this blocks every other
  // request for tens of seconds — was dwarfing the real page work. Skip when
  // there is no student context to resolve. The `student` tag still provides
  // auto-invalidation if the roster changes while the dropdown is open.
  const hasStudentContext = typeof window !== "undefined" && !!localStorage.getItem("lastStudentId");
  const { data: studentData } = useGetallStudentQuery(
    { searchText: "", id: "" },
    { skip: !hasStudentContext },
  );

  const unreadCount = noificationData?.request_count || 0;

  const userRole = React.useMemo(() => {
    if (data?.data) return (data.data as IUserInfo).user_role_name;
    return "";
  }, [data]);

  const username = React.useMemo(() => {
    if (data?.data) return (data.data as IUserInfo).username;
    return "";
  }, [data]);

  const branchName = React.useMemo(() => {
    const userData = data?.data as IUserInfo | undefined;
    return userData?.location || "";
  }, [data]);

  const staffName = React.useMemo(() => {
    if (data?.data) return (data.data as IUserInfo).staff;
    return "";
  }, [data]);

  if (isError) {
    window.location.reload();
  }

  const allcliendList = React.useMemo(() => {
    return (allclientData?.data ?? []) as ITeam[];
  }, [allclientData?.data]);

  const studentPerentList = useMemo(() => {
    return (studentData?.data ?? []) as IStudent[];
  }, [studentData?.data]);

  const clientMap = useMemo(() => {
    const map = new Map();
    allcliendList?.forEach((c) =>
      map.set(c.id, `${toProperCase(c.teamFirstName || "")} ${toProperCase(c.middle_name || "")} ${toProperCase(c.teamLastName || "")}`)
    );
    return map;
  }, [allcliendList]);

  const assignStudentMap = useMemo(() => {
    const map = new Map();
    studentPerentList?.forEach((c) =>
      map.set(c.id, `${toProperCase(c.first_name || "")} ${toProperCase(c.middle_name || "")} ${toProperCase(c.last_name || "")}`)
    );
    return map;
  }, [studentPerentList]);

  React.useEffect(() => {
    if (data?.data) {
      const userData = data.data as IUserInfo;
      dispatch(
        setCompanyInfo({
          companyLogo: userData.companyLogo || "",
          logowidthPrint: userData.logowidthPrint || 15,
          companyName: userData.companyName || "",
        })
      );
    }
  }, [data, dispatch]);

  // Scroll to top on every route change (including dynamic-id transitions
  // like /class-schedules → /class-schedules/1126, where currentMenuName
  // stays the same because both routes resolve to the same submenu).
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Sync active menu state with location.
  //
  // Historical bug (before Batch-31 sidebar polish): exact-only path match
  // meant editors and deep routes — /program-library/create, /session-data/123,
  // /settings/workflows/create, /settings/prompt-templates/{id}/edit — showed
  // NO submenu highlight AND collapsed the parent group. The spec (ABA
  // sidebar acceptance items 7 and 8) requires the parent to stay expanded
  // and the correct child to stay highlighted on every editor/detail route.
  //
  // Fix: match by prefix, longest-prefix wins. A submenu with path
  // `/program-library` matches `/program-library/create` and
  // `/program-library/edit/17`, but does NOT match `/program-library-other`
  // because we require the next character to be `/` or end-of-string.
  React.useEffect(() => {
    const path = location.pathname;
    const matches = (target: string): boolean =>
      target === path || path === target || path.startsWith(target + "/");

    let best: { menu: any; submenu: any | null; targetLen: number } | null = null;

    for (const mainMenu of MainMenuList) {
      for (const menu of mainMenu.menuList || []) {
        if (menu.path && matches(menu.path)) {
          if (!best || menu.path.length > best.targetLen) {
            best = { menu, submenu: null, targetLen: menu.path.length };
          }
        }
        for (const submenu of menu.submenus || []) {
          if ("separator" in submenu && submenu.separator) continue;
          if (submenu.path && matches(submenu.path)) {
            if (!best || submenu.path.length > best.targetLen) {
              best = { menu, submenu, targetLen: submenu.path.length };
            }
          }
          // matchPrefix wins alone even if it's shorter than another submenu's
          // exact match, but only when nothing better has been seen yet.
          if (submenu.matchPrefix && matches(submenu.matchPrefix)) {
            if (!best || submenu.matchPrefix.length > best.targetLen) {
              best = { menu, submenu, targetLen: submenu.matchPrefix.length };
            }
          }
        }
      }
    }

    if (best) {
      if (best.submenu) {
        setSelectedSubmenu(best.submenu.path);
        setExpandMenu(best.menu.title);
        setHoveredSubmenu(best.submenu.path);
        // Deep routes: also set currentMenuName so the parent renders "active".
        setCurrentMenuName(best.submenu.path);
      } else {
        setCurrentMenuName(best.menu.path);
        setSelectedSubmenu("");
        setExpandMenu(best.menu.title);
      }
    }
  }, [location.pathname]);

  // Top bar title resolver
  React.useEffect(() => {
    const findTopBarTitle = (): string => {
      const pathTitleMap: Record<string, string> = {
        "/department/create": "Create Department",
        "/branch/create": "Create Branch",
        "/user/privilege/create": "Create Privilege",
        "/user/role/new": "Create User Role",
        "/user/create": "Create User",
        "/team/create": "Create Staff",
        "/re-Schedule/list": "Reschedule List",
        "/payroll/generate": "Payroll Generate",
        "/position/create": "Position Create",
        "/email/setting/create": "Create Email Setting",
        "/email/template/create": "Email Template",
        "/request/list": "Request List",
        "/student/create": "Create Student",
        "/setting/reset-password": "Reset Password",
        "/my-profile": "My Profile",
      };

      for (const [prefix, title] of Object.entries(pathTitleMap)) {
        if (location.pathname.startsWith(prefix)) return title;
      }
      if (location.pathname.startsWith("/branch/edit/")) return "Edit Branch";
      if (location.pathname.startsWith("/team-history/")) return "Staff Profile";
      if (location.pathname.startsWith("/team/edit/")) return "Edit Staff";
      if (location.pathname.startsWith("/re-Schedule/create")) return "Create Reschedule";
      if (location.pathname.startsWith("/attendance/weekly/report")) return "Schedule Report";
      if (location.pathname.startsWith("/payroll/list/edit/")) return "Payroll Edit";
      if (location.pathname.startsWith("/request/view/")) return "Request View";
      if (location.pathname.startsWith("/student/view/")) return "Profile";
      if (location.pathname.startsWith("/student/edit/")) return "Edit Student";

      // Batch 35A §4 — this loop previously only matched EXACT paths, so
      // any deep route (e.g. /aba-therapy/programs/3) fell through and the
      // top bar reverted to today's date. Two-pass fix: (1) exact match
      // wins, (2) longest matching prefix — mirrors the sidebar-highlight
      // logic in the effect around line 1060.
      let exact: string | undefined;
      let bestPrefix: { title: string; len: number } | undefined;
      for (const mainMenu of MainMenuList) {
        for (const menu of mainMenu.menuList || []) {
          const menuTitle = menu.topBarTitle;
          if (menu.path === location.pathname) exact = menuTitle;
          if (menu.path && location.pathname.startsWith(menu.path + "/")) {
            if (menuTitle && (!bestPrefix || menu.path.length > bestPrefix.len)) {
              bestPrefix = { title: menuTitle, len: menu.path.length };
            }
          }
          for (const submenu of menu.submenus || []) {
            if ("separator" in submenu && submenu.separator) continue;
            const subTitle = submenu.topBarTitle || today;
            if (submenu.path === location.pathname) exact = subTitle;
            if (submenu.path && location.pathname.startsWith(submenu.path + "/")) {
              if (!bestPrefix || submenu.path.length > bestPrefix.len) {
                bestPrefix = { title: subTitle, len: submenu.path.length };
              }
            }
            // matchPrefix lets a submenu claim detail routes that live under a
            // different URL branch than its own path — e.g. the Attendance
            // Report list is at /attendance-record/report while individual
            // reports live at /attendance/report/:id.
            if (
              submenu.matchPrefix &&
              (location.pathname === submenu.matchPrefix ||
                location.pathname.startsWith(submenu.matchPrefix + "/"))
            ) {
              if (!bestPrefix || submenu.matchPrefix.length > bestPrefix.len) {
                bestPrefix = { title: subTitle, len: submenu.matchPrefix.length };
              }
            }
          }
        }
      }
      if (exact) return exact;
      if (bestPrefix) return bestPrefix.title;
      return today;
    };
    setTopBarTitle(findTopBarTitle());
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setOpen(!open);
    setExpandMenu("");
  };

  const handleMobileDrawerToggle = () => setMobileDrawerOpen(!mobileDrawerOpen);
  const handleMobileDrawerClose = () => setMobileDrawerOpen(false);

  const handleClickNotificationPopUp = (e: React.MouseEvent<HTMLElement>) => setNotificationPopUp(e.currentTarget);
  const handleClickProfilePopUp = (e: React.MouseEvent<HTMLElement>) => setProfilePopUp(e.currentTarget);
  const handleCloseProfilePopUp = () => setProfilePopUp(null);
  const handleCloseNotificationPopUp = () => setNotificationPopUp(null);
  const handleImgClick = () => navigate("/dashboard");

  const handleMainMenuItemClick = (ml: MenuItem) => {
    if (ml.submenus && ml.submenus.length > 0) {
      setExpandMenu(ml.title === expandMenu ? "" : ml.title);
    } else if (ml.path) {
      setCurrentMenuName(ml.path);
      setSelectedSubmenu("");
      navigate(ml.path);
      setMobileDrawerOpen(false);
    }
    setTopBarTitle(ml.topBarTitle || today);
  };

  const handleSubmenuItemClicked = (submenu: SubmenuItem) => {
    // Batch 61 §1 — separators aren't clickable; the render loop skips
    // them, but keep this narrowing so the type checker knows the
    // subsequent field accesses are safe.
    if ("separator" in submenu && submenu.separator) return;
    setCurrentMenuName(submenu.path);
    setSelectedSubmenu(submenu.path);
    navigate(submenu.path);
    setTopBarTitle(submenu.topBarTitle || today);
    setMobileDrawerOpen(false);
  };

  const lastCustomerId = localStorage.getItem("lastStaffId");
  const lastStudentId = localStorage.getItem("lastStudentId");

  // Staff "Recently Searched Student" shortcut — write + read in the same
  // component so there is no cross-hook timing gap.
  //
  // Portal predicate: URL prefix (`/staff-portal/*`), not the strict
  // role-name flag `isStaffRole`. Reason: StaffPortalRoute in Routes.tsx
  // gates access on !isSystemAdmin && !isParentUser, which admits users
  // whose role name is not literally 'staff' — so the role-name flag
  // under-triggers. The URL is the single source of truth for "am I
  // currently inside the Staff Portal?" and cannot disagree with the
  // pages the user is actually looking at.
  const isInStaffPortal = location.pathname.startsWith("/staff-portal");
  const [staffLastStudentId, setStaffLastStudentId] = React.useState<string | null>(
    () => localStorage.getItem("staffLastStudentId")
  );
  React.useEffect(() => {
    const m = location.pathname.match(/^\/staff-portal\/students\/(\d+)(?:\/|$)/);
    if (!m) return;
    const id = m[1];
    if (localStorage.getItem("staffLastStudentId") !== id) {
      localStorage.setItem("staffLastStudentId", id);
    }
    setStaffLastStudentId((prev) => (prev === id ? prev : id));
  }, [location.pathname]);

  // Parent users get their own layout
  if (isParentUser) {
    return <>{children}</>;
  }

  // ── Shared sidebar content props ──
  const sidebarProps: SidebarMenuContentProps = {
    open,
    expandMenu,
    currentMenuName,
    selectedSubmenu,
    hoveredMenuName,
    hoveredSubmenu,
    setHoveredMenuName,
    setHoveredSubmenu,
    handleMainMenuItemClick,
    handleSubmenuItemClicked,
    userHasRight,
    isParentUser,
    isSystemAdmin,
    isStaffRole,
  };

  // ── Shared top bar action props ──
  const topBarActionProps: TopBarActionsProps = {
    classes,
    lastStudentId,
    lastCustomerId,
    openTextStudent,
    openText,
    assignStudentMap,
    clientMap,
    setOpenTextStudent,
    setOpenText,
    handleClickNotificationPopUp,
    toggleTheme,
    mode,
    handleClickProfilePopUp,
    unreadCount,
    isStaffRole,
    staffLastStudentId,
    isInStaffPortal,
  };

  // ── Popups ──
  const popups = (
    <>
      <Profile
        profile={profilePopUp}
        onClose={handleCloseProfilePopUp}
        open={openProfilePopUp}
        username={username}
        staffName={staffName}
        userRole={[branchName, userRole].filter(Boolean).join(" / ")}
      />
      <Notification
        notification={notificationPopUp}
        onClose={handleCloseNotificationPopUp}
        open={openNotificationPopUp}
      />
    </>
  );

  // ── Drawer Header Content ──
  // White-label header. Expanded state renders the horizontal primary
  // logo with aspect ratio preserved (max 180×55) so wordmarks are
  // legible; collapsed state renders the compact/favicon mark at 34×34
  // so a horizontal logo never gets squeezed into a square cell.
  // Wordmark comes from companySlice.companyName so distinct tenants
  // render distinct branding without a code change.
  const renderDrawerHeader = (showClose?: boolean) => (
    <DrawerHeader>
      {open ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            flexShrink: 0,
            px: 0.5,
            minWidth: 0,
            flex: "1 1 auto",
          }}
          onClick={handleImgClick}
        >
          {primaryLogoSrc ? (
            <Box
              component="img"
              src={primaryLogoSrc}
              alt={companyName || "Company Logo"}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              sx={{
                maxWidth: 180,
                maxHeight: 55,
                width: "auto",
                height: "auto",
                objectFit: "contain",
                flexShrink: 1,
                minWidth: 0,
              }}
            />
          ) : (
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 15,
                color: "#0f3557",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                lineHeight: 1.15,
                whiteSpace: "nowrap",
                display: "block",
                flexShrink: 0,
              }}
            >
              {(companyName || "Portal").toUpperCase()}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "center", cursor: "pointer" }}
          onClick={handleImgClick}
        >
          {compactLogoSrc ? (
            <Box
              component="img"
              src={compactLogoSrc}
              alt={companyName || "Company Logo"}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              sx={{
                width: 34,
                height: 34,
                objectFit: "contain",
              }}
            />
          ) : (
            <Box
              aria-hidden
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.35)",
              }}
            />
          )}
        </Box>
      )}
      <IconButton
        onClick={showClose ? handleMobileDrawerClose : handleDrawerToggle}
        sx={{
          width: 30, height: 30, borderRadius: "8px",
          color: alpha("#fff", 0.5),
          "&:hover": { bgcolor: alpha("#fff", 0.08), color: "#fff" },
        }}
      >
        {showClose ? (
          <CloseIcon sx={{ fontSize: 18 }} />
        ) : open ? (
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>
    </DrawerHeader>
  );

  // ── Full-screen routes ──
  const isFullScreenRoute =
    currentMenuNameFullScreen === "/order/list/create" ||
    currentMenuNameFullScreen === "/order/list/edit/:id" ||
    currentMenuNameFullScreen === "/order/list/view/:id" ||
    currentMenuNameFullScreen === "/order/list/price/admin/edit/:id" ||
    currentMenuNameFullScreen === "/order/list/set/price/edit/:id" ||
    currentMenuNameFullScreen === "/order/list/set/price/list/:id" ||
    currentMenuNameFullScreen === "/schedule/list/calender/one-day" ||
    currentMenuNameFullScreen === "/attendance/weekly/report" ||
    currentMenuNameFullScreen === "/order/list/sentToManager/edit/:id";

  return (
    <Box sx={{ display: "flex" }}>
      {!isMobile ? (
        // ═══ DESKTOP LAYOUT ═══
        <Box sx={{ display: "flex" }}>
          {isFullScreenRoute ? (
            <>
              <AppBarFullScreen position="fixed" className={`${classes.mobileTopbar} no-print`}>
                <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%", px: 2 }}>
                  {primaryLogoSrc && (
                    <img
                      src={primaryLogoSrc}
                      alt={companyName || "Company Logo"}
                      style={{ height: 32, maxWidth: 180, width: "auto", cursor: "pointer", objectFit: "contain" }}
                      onClick={handleMobileDrawerToggle}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  )}
                  <IconButton
                    onClick={handleMobileDrawerToggle}
                    edge="start"
                    sx={{ ...(mobileDrawerOpen && { display: "none" }), ml: 2, borderRadius: "10px", width: 38, height: 38 }}
                  >
                    {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
                  </IconButton>
                  <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 16, color: theme.palette.text.secondary, ml: 2 }}>
                    {topBarTitle}
                  </Typography>
                  <TopBarActions {...topBarActionProps} />
                  {popups}
                </Toolbar>
                <MobileDrawer open={mobileDrawerOpen} onClose={handleMobileDrawerClose} className="no-print">
                  {renderDrawerHeader(true)}
                  <DrawerContent>
                    <SidebarMenuContent {...sidebarProps} />
                  </DrawerContent>
                </MobileDrawer>
              </AppBarFullScreen>
            </>
          ) : (
            <>
              <CssBaseline />
              <AppBar position="fixed" open={open} className={`${classes.topbar} no-print`}
                sx={{
                  height: `${headerHeight}px !important`,
                  bgcolor: `${theme.palette.topbarButton.topbarBg} !important`,
                  boxShadow: "none !important",
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                }}
              >
                <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%", px: 2.5, minHeight: `${headerHeight}px !important` }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 17, color: theme.palette.text.secondary }}>
                    {topBarTitle}
                  </Typography>
                  <TopBarActions {...topBarActionProps} />
                  {popups}
                </Toolbar>
              </AppBar>
              <IsNotMobileDrawer variant="permanent" open={open} className="no-print">
                {renderDrawerHeader()}
                <DrawerContent>
                  <SidebarMenuContent {...sidebarProps} />
                </DrawerContent>
              </IsNotMobileDrawer>
            </>
          )}
        </Box>
      ) : (
        // ═══ MOBILE LAYOUT ═══
        <Box sx={{ display: "flex" }}>
          <AppBar position="fixed" className={`${classes.mobileTopbar} no-print`}
            sx={{
              height: `${headerHeight}px !important`,
              bgcolor: `${theme.palette.cardAdd.default} !important`,
              boxShadow: "none !important",
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
            }}
          >
            <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%", px: 1.5, minHeight: `${headerHeight}px !important` }}>
              <IconButton
                onClick={handleMobileDrawerToggle}
                edge="start"
                sx={{ ...(mobileDrawerOpen && { display: "none" }), borderRadius: "10px", width: 40, height: 40 }}
              >
                {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <TopBarActions {...topBarActionProps} isMobileView navigate={navigate} />
              {popups}
            </Toolbar>

            <MobileDrawer open={mobileDrawerOpen} onClose={handleMobileDrawerClose} className="no-print">
              {renderDrawerHeader(true)}
              <DrawerContent>
                <SidebarMenuContent {...sidebarProps} />
              </DrawerContent>
            </MobileDrawer>
          </AppBar>
        </Box>
      )}

      {/* ── Main Content Area ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Flex items default to min-width:auto, which lets this column grow to
          // its content's intrinsic width and overflow the viewport (notably at
          // tablet widths). minWidth:0 lets it shrink to the available flex space
          // so wide children wrap/scroll within their own containers instead.
          minWidth: 0,
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2,
          // Extra bottom space so the floating chat button never overlaps
          // page action buttons (Save/Update) sitting at the bottom-right.
          // Batch 32 §11.6 — bumped from 11/12 (88/96px) to clear a 56px FAB at
          // 24px offset plus a 40px action button on top of it.
          pb: { xs: "128px", md: "112px" },
          minHeight: "100vh",
          // Batch 40 — every /aba-therapy/* route runs on a clean white
          // canvas. The mint palette.background.default stays for the rest
          // of the app (Dashboard, Students, Classes, Attendance, …). One
          // conditional in the shell instead of per-page background patches.
          backgroundColor: location.pathname.startsWith("/aba-therapy")
            ? "#FFFFFF"
            : theme.palette.background.default,
        }}
      >
        <PaddingBody />
        {children}
      </Box>
    </Box>
  );
}
